import React, { useEffect, useRef, useCallback, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { getOptions, getFirstOption, getDefaultOption } from "utils/fields";
import { required, maxLength, getValidation } from "utils/fieldValidation";
import { standardCodeLogic } from "utils/codes";
import { getModelDataComponentPath } from "utils/enclosure/codeDrawerMeasurements";
import { getSelectedComponents } from "utils/parametric";
import floorsAboveCodeTemplate from "features/Model/Enclosure/Templates/floorsAboveCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";
import Field from "components/Field";
import Button from "components/Button";
import Tooltip from "components/Tooltip";
import PercentGradeSwitch from "components/EmbodiedCarbonGrade/PercentGradeSwitch";
import EmbodiedCarbonFramingGroupGrade, {
    EmbodiedCarbonCalculationsSummary,
    StandardEmbodiedLayerBlock,
} from "components/EmbodiedCarbonGrade";

import globalStyles from "components/globalStyles.module.scss";

// import wallCrossSection from "assets/images/Wall-Cross-Section.svg"; // UPDATE WHEN READY

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode, getRValue } = enclosureActions;
const { updateUpgradeCodes } = upgradeActions;

const charLim32 = maxLength(32);

const labelValidation = [required, charLim32];
const interiorValidation = getValidation("csFloorsAboveInterior");
const structureValidation = getValidation("csFloorsAboveStructureType");
const typeSizeValidation = getValidation("csFloorsAboveComponentTypeSize");
const sheathingValidation = getValidation("csFloorsAboveSheathing");
const framingSpacingValidation = getValidation("csFloorsAboveFramingSpacing");
const exteriorValidation = getValidation("csFloorsAboveExterior");
const ins1Validation = getValidation("csFloorsAboveInsLayer1");
const ins2Validation = getValidation("csFloorsAboveInsLayer2");
const dropFramingValidation = getValidation("csFloorsAboveDropFraming");

const StandardFloorsAboveCode = React.memo(
    ({
        toggleOpen,
        componentId,
        fieldAccessor,
        isCodeLibrary = false,
        change,
        upgradeWhereToAddCode = {},
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        removeFormErrors,
        CodeTypeSelect,
    }) => {
        const [barMetric, setBarMetric] = useState("percentage");

        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};
        const selectedComponentsForUpgrade = useStateSelector(`newForm.upgradeDrawer.selectedComponents`) || [];

        const uid = useStateSelector(`user.uid`);
        const { floorsAboveInsType = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { modelId, modelData: { codes: modelCodes = {}, components = {} } = {} } =
            useStateSelector(`model`) || {};
        const { components: componentsFromParametric = {} } =
            useStateSelector(`parametricAnalysis.parametricAnalysis.model.modelData`) || {};

        const floorsAboveCode = useStateSelector(`${drawerAccessor}.floorsAboveCode`) || {};
        const { id: floorsAboveStructureId, value: floorsAboveStructureValue = 0 } =
            useStateSelector(`${drawerAccessor}.floorsAboveCode.layers.structureType`) || {};
        const { id: floorsAboveSpacingId, value: floorsAboveSpacingValue } =
            useStateSelector(`${drawerAccessor}.floorsAboveCode.layers.spacing`) || {};
        const { id: floorsAboveDropFramingId, value: floorsAboveDropFramingValue } =
            useStateSelector(`${drawerAccessor}.floorsAboveCode.layers.dropFraming`) || {};
        const { value: floorsAboveTypeSizeValue } =
            useStateSelector(`${drawerAccessor}.floorsAboveCode.layers.componentTypeSize`) || {};
        const { value: floorsAboveFramingValue } =
            useStateSelector(`${drawerAccessor}.floorsAboveCode.layers.framing`) || {};
        const { value: floorsAboveIns1Value } =
            useStateSelector(`${drawerAccessor}.floorsAboveCode.layers.insulationLayer1`) || {};
        const { value: floorsAboveIns2Value } =
            useStateSelector(`${drawerAccessor}.floorsAboveCode.layers.insulationLayer2`) || {};
        const { value: floorsAboveInteriorValue } =
            useStateSelector(`${drawerAccessor}.floorsAboveCode.layers.interior`) || {};
        const { value: floorsAboveSheathingValue } =
            useStateSelector(`${drawerAccessor}.floorsAboveCode.layers.sheathing`) || {};
        const { value: floorsAboveExteriorValue } =
            useStateSelector(`${drawerAccessor}.floorsAboveCode.layers.exterior`) || {};
        const floorsAboveLabel = useStateSelector(`${drawerAccessor}.floorsAboveCode.label`) || "";
        const floorsAboveLayers = useStateSelector(`${drawerAccessor}.floorsAboveCode.layers`) || {};
        const layersFieldPrefix = `${drawerAccessor}.floorsAboveCode.layers`;

        const frameSpacingValue = floorsAboveSpacingId === null ? floorsAboveFramingValue : floorsAboveSpacingValue;
        const floorsAboveValue = `4${floorsAboveStructureValue
            .toString()
            .substr(
                -1
            )}${floorsAboveTypeSizeValue}${frameSpacingValue}${floorsAboveIns1Value}${floorsAboveIns2Value}${floorsAboveInteriorValue}${floorsAboveSheathingValue}${floorsAboveExteriorValue}${
            floorsAboveDropFramingValue || 0
        }`;

        const isSolid = parseFloat(floorsAboveStructureId) === 4;
        const isPanel = parseFloat(floorsAboveStructureId) === 5;
        const isDropFraming = parseFloat(floorsAboveDropFramingId) === 1;

        const makeSpacingDefault = ["5", "6", "7", "8", "9", "A", "B", "C"].includes(floorsAboveTypeSizeValue);
        const makeInsulationDefault = ["6", "7", "8", "9", "A", "B", "C"].includes(floorsAboveTypeSizeValue);
        const makeDropFramingInsDefault = ["9", "A", "E", "F", "U", "M", "N", "O", "P", "Q", "R", "S", "T"].includes(
            floorsAboveIns1Value
        );

        const { codeRef: editingCodeRef = null, isLibCode = false } = floorsAboveInsType;
        const isEditing = !!editingCodeRef;
        const isUpgradeComponent = fieldAccessor.includes("fields.");

        const componentPath = isUpgradeComponent ? "" : getModelDataComponentPath("FloorsAbove", fieldAccessor);
        const selectedComponents = isUpgradeComponent ? selectedComponentsForUpgrade : [componentPath];

        const { upgradeSubType, upgradeType } = upgradeWhereToAddCode?.ecmInputMapUpgrade || {};

        const embcModelComponents = modelId ? components : componentsFromParametric;
        const embcSelectedComponents = isEqual(selectedComponents, ["*"])
            ? getSelectedComponents(upgradeSubType, upgradeType, embcModelComponents)
            : selectedComponents;

        const valueRef = useRef(null);

        const handleStuctureChange = useCallback(
            ({ id, value }) => {
                change(
                    `${drawerAccessor}.floorsAboveCode.layers.insulationLayer1`,
                    getFirstOption({ indKey: id, depKey: "csFloorsAboveInsLayer1" })
                );
                change(
                    `${drawerAccessor}.floorsAboveCode.layers.componentTypeSize`,
                    getFirstOption({ indKey: id, depKey: "csFloorsAboveComponentTypeSize" })
                );

                // Use "Framing" when set to Solid, use Spacing otherwise
                if (parseFloat(id) === 4) {
                    //set spacing to null and grab first option for framing
                    change(`${drawerAccessor}.floorsAboveCode.layers.spacing`, { id: null });
                    change(
                        `${drawerAccessor}.floorsAboveCode.layers.framing`,
                        getFirstOption({
                            indKey: id,
                            depKey: "csFloorsAboveFramingSpacing",
                        })
                    );
                } else {
                    //set framing to null and grab first option for spacing
                    change(`${drawerAccessor}.floorsAboveCode.layers.framing`, { id: null });

                    // Only grab default spacing id if not set
                    if (floorsAboveSpacingId === null) {
                        change(
                            `${drawerAccessor}.floorsAboveCode.layers.spacing`,
                            getDefaultOption("csFloorsAboveFramingSpacing")
                        );
                    }
                }

                // Panel
                if (parseFloat(id) === 5) {
                    change(
                        `${drawerAccessor}.floorsAboveCode.layers.insulationLayer1`,
                        getDefaultOption("csFloorsAboveInsLayer1")
                    );
                    change(
                        `${drawerAccessor}.floorsAboveCode.layers.insulationLayer2`,
                        getDefaultOption("csFloorsAboveInsLayer2")
                    );
                    change(
                        `${drawerAccessor}.floorsAboveCode.layers.spacing`,
                        getDefaultOption("csFloorsAboveFramingSpacing")
                    );
                    change(
                        `${drawerAccessor}.floorsAboveCode.layers.dropFraming`,
                        getDefaultOption("csFloorsAboveDropFraming")
                    );
                }
            },
            [floorsAboveStructureId, change, floorsAboveSpacingId]
        );

        useEffect(() => {
            const initialValues = {
                floorsAboveCode: isEmpty(floorsAboveInsType) ? floorsAboveCodeTemplate : floorsAboveInsType,
                addToLibrary: isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        useEffect(() => {
            valueRef.current = floorsAboveValue;
        }, [floorsAboveValue, floorsAboveLabel]);

        //Filter Insulation Layer 1 for drop framing
        const baseInsLayer1Opts = [
            "0",
            "K",
            "1",
            "2",
            "X",
            "Y",
            "3",
            "4",
            "Z",
            "a",
            "5",
            "L",
            "c",
            "J",
            "b",
            "d",
            "V",
            "W",
            "I",
            "e",
        ];
        let insLayer1Opts = getOptions({ fieldKey: "csFloorsAboveInsLayer1", indKey: floorsAboveStructureId });
        if (isDropFraming) {
            insLayer1Opts = insLayer1Opts.filter((opt) => baseInsLayer1Opts.includes(opt.value.value));
        }

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        const floorsAboveEmbRowStyle = {
            border: "1px solid #E0E0E0",
            borderRadius: "4px",
            marginBottom: "4px",
        };

        return (
            <div className={globalStyles.standardCodeWrapper}>
                {/* <h3>Standard Code</h3> */}
                <InputRow gridTemplate={CodeTypeSelect ? "1fr 1fr 1fr" : "1fr 1fr"} style={{ alignItems: "flex-end" }}>
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.floorsAboveCode.label`}
                        label="Code Label"
                        placeholder="Code Label"
                        validate={labelValidation}
                        setValue={floorsAboveLabel.toString() === valueRef.current ? floorsAboveValue : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.floorsAboveCode.value`}
                        label="Internal Code"
                        placeholder="###########"
                        disabled={true}
                        setValue={floorsAboveValue}
                    />
                    {!isCodeLibrary && (
                        <>
                            <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                                <Field
                                    component={Checkbox}
                                    name={`${drawerAccessor}.addToLibrary`}
                                    label="Add to Code Library"
                                    large
                                    disabled={isLibCode}
                                    type="checkbox"
                                />
                            </div>
                            <Field
                                component={Checkbox}
                                name={`${drawerAccessor}.setAsModelDefault`}
                                label={"Set as model default"}
                                large
                                type="checkbox"
                                style={{ whiteSpace: "nowrap" }}
                            />
                        </>
                    )}
                </InputRow>

                <div className={globalStyles.assemblyLayersHeading}>
                    <h4>Assembly Layers</h4>
                    <PercentGradeSwitch activeTab={barMetric} setActiveTab={setBarMetric} />
                </div>

                <div className={globalStyles.framingGroup}>
                    <InputRow
                        style={{
                            gridGap: "0",
                            border: "1px solid #e1eaf0",
                            borderBottom: "none",
                            borderRadius: "4px 4px 0px 0px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.floorsAboveCode.layers.structureType`}
                            label="Structure Type"
                            placeholder="Choose Structure Type"
                            options={getOptions({ fieldKey: "csFloorsAboveStructureType" })}
                            validate={structureValidation}
                            onChange={handleStuctureChange}
                            isFlatDesign={true}
                            borderBottom={true}
                            customBorderRadius={{ borderRadius: "4px 4px 0px 0px" }}
                        />
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.floorsAboveCode.layers.componentTypeSize`}
                            label="Component Type / Size"
                            placeholder="Choose Type / Size"
                            options={getOptions({
                                fieldKey: "csFloorsAboveComponentTypeSize",
                                indKey: floorsAboveStructureId,
                            })}
                            validate={typeSizeValidation}
                            onChange={({ value = "" }) => {
                                if (["6", "7", "8", "9", "A", "B", "C"].includes(value)) {
                                    change(
                                        `${drawerAccessor}.floorsAboveCode.layers.insulationLayer1`,
                                        getDefaultOption("csFloorsAboveInsLayer1")
                                    );
                                    change(
                                        `${drawerAccessor}.floorsAboveCode.layers.insulationLayer2`,
                                        getDefaultOption("csFloorsAboveInsLayer2")
                                    );
                                }
                                if (["5", "6", "7", "8", "9", "A", "B", "C"].includes(value)) {
                                    change(`${drawerAccessor}.floorsAboveCode.layers.framing`, { id: 0, value: "0" });
                                }
                            }}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <InputRow
                        style={{
                            gridGap: "0",
                            borderBottom: "1px solid #e1eaf0",
                            borderLeft: "1px solid #e1eaf0",
                            borderRight: "1px solid #e1eaf0",
                            borderTop: "none",
                            borderRadius: "0px 0px 4px 4px",
                        }}
                        lastRow={true}
                    >
                        {isSolid ? (
                            <Field
                                component={Select}
                                name={`${drawerAccessor}.floorsAboveCode.layers.framing`}
                                label="Framing"
                                placeholder="Choose Framing"
                                options={getOptions({
                                    fieldKey: "csFloorsAboveFramingSpacing",
                                    indKey: floorsAboveStructureId,
                                })}
                                validate={framingSpacingValidation}
                                disabled={makeSpacingDefault}
                                isFlatDesign={true}
                                borderBottom={true}
                            />
                        ) : (
                            <Field
                                component={Select}
                                name={`${drawerAccessor}.floorsAboveCode.layers.spacing`}
                                label="Spacing"
                                placeholder="Choose Spacing"
                                options={getOptions({
                                    fieldKey: "csFloorsAboveFramingSpacing",
                                    indKey: floorsAboveStructureId,
                                })}
                                validate={framingSpacingValidation}
                                disabled={isPanel}
                                isFlatDesign={true}
                                borderBottom={true}
                            />
                        )}
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.floorsAboveCode.layers.dropFraming`}
                            label="Drop Framing"
                            placeholder="Choose"
                            options={getOptions({ fieldKey: "csFloorsAboveDropFraming" })}
                            validate={dropFramingValidation}
                            disabled={isPanel}
                            onChange={({ value = "" }) => {
                                if (makeDropFramingInsDefault && value == 1) {
                                    change(
                                        `${drawerAccessor}.floorsAboveCode.layers.insulationLayer1`,
                                        getDefaultOption("csFloorsAboveInsLayer1")
                                    );
                                }
                            }}
                            isFlatDesign={true}
                            customBorderRadius={{ borderRadius: "0px 0px 4px 4px" }}
                        />
                    </InputRow>
                    <EmbodiedCarbonFramingGroupGrade
                        layers={floorsAboveLayers}
                        componentType="floorsAbove"
                        customPadding="4px 0 0 0"
                        change={change}
                        layersFieldPrefix={layersFieldPrefix}
                        customGap={"1rem"}
                        barMetric={barMetric}
                    />
                </div>

                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer1"
                    layers={floorsAboveLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="floorsAbove"
                    editorDisabled={isPanel || makeInsulationDefault}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={floorsAboveEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.floorsAboveCode.layers.insulationLayer1`}
                        label="Framing Cavity Insulation (Layer 1)"
                        placeholder="Choose Insulation"
                        options={insLayer1Opts}
                        validate={ins1Validation}
                        disabled={isPanel || makeInsulationDefault}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer2"
                    layers={floorsAboveLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="floorsAbove"
                    editorDisabled={isPanel || makeInsulationDefault}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={floorsAboveEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.floorsAboveCode.layers.insulationLayer2`}
                        label="Continuous Layer of Insulation (Layer 2)"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: "csFloorsAboveInsLayer2" })}
                        validate={ins2Validation}
                        disabled={isPanel || makeInsulationDefault}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <StandardEmbodiedLayerBlock
                    layerType="interior"
                    layers={floorsAboveLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="floorsAbove"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={floorsAboveEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.floorsAboveCode.layers.interior`}
                        label="Interior"
                        placeholder="Choose Interior"
                        options={getOptions({ fieldKey: "csFloorsAboveInterior" })}
                        validate={interiorValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="sheathing"
                    layers={floorsAboveLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="floorsAbove"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={floorsAboveEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.floorsAboveCode.layers.sheathing`}
                        label="Sheathing"
                        placeholder="Choose Sheathing"
                        options={getOptions({ fieldKey: "csFloorsAboveSheathing" })}
                        validate={sheathingValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="exterior"
                    layers={floorsAboveLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="floorsAbove"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={{ ...floorsAboveEmbRowStyle, marginBottom: 0 }}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.floorsAboveCode.layers.exterior`}
                        label="Exterior"
                        placeholder="Choose Exterior"
                        options={getOptions({ fieldKey: "csFloorsAboveExterior" })}
                        validate={exteriorValidation}
                        info={
                            "The Exterior should only be modelled if more than 50% of the total underside of the joists have a finished ceiling (e.g. suspended ceiling, gypsum, wood). In such cases, model the entire floor above foundation's Exterior component with the finished ceiling material. Model suspended ceiling tiles as 12 mm (0.5 in.) gypsum board."
                        }
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <EmbodiedCarbonCalculationsSummary
                    componentId={componentId}
                    code={floorsAboveCode}
                    componentType={"floorsAbove"}
                    modelDataComponents={embcModelComponents}
                    selectedComponents={embcSelectedComponents}
                />

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            toggleOpen(false);
                            removeFormErrors(`${drawerAccessor}.floorsAboveCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                toggleOpen,
                                uid,
                                fieldAccessor,
                                editingCodeRef,
                                isCodeLibrary,
                                componentId,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear wall code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                <Tooltip id="codeLibTip" hide={!isLibCode} />
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "floorsAboveInsType")),
    handleSubmit: ({
        change,
        codeFields,
        toggleOpen,
        uid,
        fieldAccessor,
        editingCodeRef,
        isCodeLibrary,
        componentId,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const {
            floorsAboveCode = {},
            addToLibrary = false,
            setAsModelDefault = false,
            selectedComponents = [],
        } = codeFields;

        toggleOpen(false);

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        // Run standard code logic
        standardCodeLogic({
            change,
            componentCodeType: "FloorsAbove",
            componentId,
            code: floorsAboveCode,
            inputName: "floorsAboveInsType",
            codeType: "S",
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            getRValue,
            fieldAccessor,
            components,
            modelCodes,
            addToLibrary,
            addToCodeLib,
            uid,
            deleteCode,
            updateUpgradeCodes,
            setAsModelDefault,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardFloorsAboveCode);
